/**
 * AccessToken 만료 판정 기준.
 *
 * 원칙: 서버가 발급한 JWT의 `exp` 클레임을 **단일 출처**로 삼는다.
 * 클라이언트가 임의 상수로 만료를 추정하면 서버 실제 만료(현재 access 1d)와
 * 어긋나 만료 토큰을 유효로 오판하고, 401 복구 경로가 같은 만료 토큰을 재사용해
 * 세션이 끊기는 문제가 생긴다. 아래 상수는 `exp` 디코드에 실패한 경우의
 * **최후 폴백**일 뿐이다.
 */
const isDev = process.env.NODE_ENV === 'development';

const MINUTES = 60 * 1000;

/**
 * 만료 스큐: 실제 `exp`보다 이만큼 앞서 만료로 간주해 미리 갱신한다.
 * 요청이 서버로 가는 도중 토큰이 만료돼 401이 나는 창을 줄인다.
 */
export const EXPIRY_SKEW_MS = 30 * 1000;

/**
 * `exp` 디코드 실패 시에만 쓰는 폴백 TTL (ms).
 * 서버 실제값(access 1d)보다 보수적으로 짧게 잡아, 오판 시에도 refresh가
 * 빨리 트리거되도록 한다.
 */
const FALLBACK_TTL_MS = isDev ? 15 * MINUTES : 30 * MINUTES;

/** base64url → 문자열 디코드 (브라우저 atob / Node Buffer 모두 대응) */
function decodeBase64Url(segment: string): string | null {
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    );
    if (typeof atob === 'function') {
      return atob(padded);
    }
    // SSR 등 atob 미존재 환경 폴백
    return Buffer.from(padded, 'base64').toString('binary');
  } catch {
    return null;
  }
}

/**
 * JWT의 `exp`(초 단위)를 Unix ms로 반환. 파싱 실패 시 null.
 * 서명은 검증하지 않는다 — 만료 시각 추정 용도이며, 실제 검증은 서버가 한다.
 */
export function decodeJwtExpiresAtMs(token: string | undefined): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const json = decodeBase64Url(parts[1]);
  if (!json) return null;
  try {
    const payload = JSON.parse(json) as { exp?: number };
    if (typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) {
      return null;
    }
    return payload.exp * 1000;
  } catch {
    return null;
  }
}

/**
 * AccessToken의 만료 시각(Unix ms). JWT `exp` 우선, 실패 시 폴백 TTL.
 * 반환값에는 스큐가 이미 반영돼 있어, 이 시각을 지나면 만료로 간주하면 된다.
 */
export function getAccessTokenExpiresAt(token: string): number {
  const expMs = decodeJwtExpiresAtMs(token);
  const base = expMs ?? Date.now() + FALLBACK_TTL_MS;
  return base - EXPIRY_SKEW_MS;
}

/**
 * JWT 의 `sub`(회원 번호). 파싱 실패·없음이면 null.
 * 서명은 검증하지 않는다 — 앱과 웹이 같은 회원인지 비교하는 용도다.
 */
export function decodeJwtSubject(token: string | undefined): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const json = decodeBase64Url(parts[1]);
  if (!json) return null;
  try {
    const sub = (JSON.parse(json) as { sub?: unknown }).sub;
    const n = typeof sub === 'string' ? Number(sub) : sub;
    return typeof n === 'number' && Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}
